import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { GatedAssetForm } from '@/components/forms/GatedAssetForm';
import { FileArchive, CheckCircle2, UserCheck } from 'lucide-react';

export function ToolkitTeamAlignmentPage() {
  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground relative">
      <Helmet>
        <title>Team Alignment Workshop Guide | FIVE LENS Enneagram</title>
        <meta name="description" content="Step-by-step facilitation guide for running an introductory FIVE LENS alignment workshop with newly formed leadership teams." />
        <meta property="og:title" content="Team Alignment Workshop Guide | FIVE LENS Enneagram" />
        <meta property="og:description" content="Step-by-step facilitation guide for running an introductory FIVE LENS alignment workshop with newly formed leadership teams." />
        <link rel="canonical" href="https://www.fivelensenneagram.com/resource-library/team-alignment-workshop-guide" />
      </Helmet>

      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: 'Resource Library', href: '/resource-library' },
          { label: 'Team Alignment Workshop Guide' }
        ]} />

        <div className="mt-8 mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-rule-3 bg-ground-alt border border-ground-deep px-3 py-1 mb-4 rounded-sm">
            <FileArchive className="w-3.5 h-3.5" /> Workshop Guide
          </div>
          <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-6">
            Team Alignment Workshop Guide
          </h1>
          <p className="text-xl text-ink/80 max-w-3xl leading-relaxed font-light">
            Step-by-step facilitation guide for running an introductory FIVE LENS alignment workshop with newly formed leadership teams.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Details */}
          <div className="lg:col-span-7 space-y-8">
            <div className="bg-white border border-ground-deep p-8 shadow-sm">
              <h2 className="text-xl font-serif text-ink mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-rule-3" /> Who it&apos;s for
              </h2>
              <p className="text-ink/80 text-sm leading-relaxed">
                HR business partners, OD specialists, facilitators and team leaders of newly formed leadership teams.
              </p>
            </div>

            <div className="bg-white border border-ground-deep p-8 shadow-sm">
              <h2 className="text-xl font-serif text-ink mb-6">What&apos;s inside</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    A complete 3½-hour agenda with timings and facilitator scripts
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    Four modules: how we decide (Energy Centres), what we need (Social Drives), where we are going, and how we will work
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    Pre-work questions, room set-up and facilitator watch-outs
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    Templates: working agreements, decision rights and a stop list
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/80 leading-relaxed">
                    A follow-up plan for the first three months
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="lg:col-span-5 bg-white border border-ground-deep p-8 shadow-md sticky top-32">
            <h3 className="text-2xl font-serif text-ink mb-2">Get the Guide</h3>
            <p className="text-xs text-ink/70 mb-6 leading-relaxed">
              Complete the form below to instantly access the PDF workshop guide download.
            </p>
            <GatedAssetForm 
              assetName="Team Alignment Workshop Guide" 
              mailchimpTag="Asset: Team Alignment Guide" 
              buttonText="Request Guide PDF" 
              eventName="toolkit_download_request" 
              ctaPosition="team_alignment_guide_page" 
              downloadUrl="/downloads/Team-Alignment-Workshop-Guide.pdf" 
              consentLabel="Email me the guide, plus occasional FIVE LENS insights and event invitations."
            />
          </div>
        </div>
      </div>
    </main>
  );
}
